import React from 'react';

import { creditedOnly } from './queries';
import { postTrafficQuality, QualityError } from './quality/api';
import { facetRequest } from './quality/compile';
import { enqueueQuality } from './quality/queue';
import type {
  QualityDiscoveryFacet,
  QualityErrorCode,
  QualityFacetData,
  QualityFacetRequest,
  QualityFilters,
  QualityRange,
} from './quality/types';

export type FacetName = QualityDiscoveryFacet['name'];
export type FacetData<N extends FacetName> = Extract<QualityFacetData, { name: N }>;
export type LinkGroups = FacetData<'link_groups'>;

/** One picker's counts; `error` and `idle` show `-` on every choice, never 0. */
export type FacetSlot<T> = {
  status: 'idle' | 'loading' | 'ready' | 'error';
  data: T | null;
  /** The relay's code for a failed read, when it gave one (`filter_limit_exceeded`: the draft is too large). */
  code?: QualityErrorCode;
};

export type QualityFacetsInput = {
  tenantId: number | undefined;
  /** Quality mode is on for the brand. */
  enabled: boolean;
  /** The report's `scope.selectedHosts` once it has settled; null before. An empty list asks nothing. */
  selectedHosts: readonly string[] | null;
  range: QualityRange;
  /** The Filters sheet's draft. */
  filters: QualityFilters;
  /** The Filters sheet is open: a facet it showed stays asked for until it closes, so its read lands. */
  open: boolean;
  /** The facets the open sheet shows now. */
  wanted: readonly FacetName[];
};

export type QualityFacetsState = {
  sources: FacetSlot<FacetData<'source'>>;
  links: FacetSlot<FacetData<'link'>>;
  countries: FacetSlot<FacetData<'country'>>;
  linkGroups: FacetSlot<LinkGroups>;
};

const IDLE = { status: 'idle', data: null } as const;
const LOADING = { status: 'loading', data: null } as const;
const ERROR = { status: 'error', data: null } as const;

const sorted = (values: readonly string[]): string[] => [...values].sort();
const byKey =
  <T>(key: (v: T) => string) =>
  (a: T, b: T): number =>
    key(a) < key(b) ? -1 : key(a) > key(b) ? 1 : 0;

/** The filters with every field in one fixed order and every value list sorted, so one selection is one signature. */
const canonicalFilters = (f: QualityFilters): QualityFilters => ({
  ...(f.pages
    ? {
        pages: {
          mode: f.pages.mode,
          items: f.pages.items.map(({ kind, path }) => ({ kind, path })).sort(byKey((p) => `${p.kind}:${p.path}`)),
        },
      }
    : {}),
  ...(f.channels
    ? {
        channels: f.channels
          .map((c) => ({
            channel: c.channel,
            platforms: c.platforms === 'all' ? ('all' as const) : sorted(c.platforms),
            except: sorted(c.except),
          }))
          .sort(byKey((c) => c.channel)),
      }
    : {}),
  ...(f.links
    ? {
        links: {
          affiliates: f.links.affiliates
            .map((a) => ({ id: a.id, exceptLinks: sorted(a.exceptLinks) }))
            .sort(byKey((a) => a.id)),
          exact: sorted(f.links.exact),
          noLink: f.links.noLink,
          ...(f.links.allExcept ? { allExcept: sorted(f.links.allExcept) } : {}),
          ...(f.links.exceptAffiliates ? { exceptAffiliates: sorted(f.links.exceptAffiliates) } : {}),
        },
      }
    : {}),
  ...(f.campaigns ? { campaigns: { mode: f.campaigns.mode, values: sorted(f.campaigns.values) } } : {}),
  ...(f.countries ? { countries: { mode: f.countries.mode, values: sorted(f.countries.values) } } : {}),
});

/** Each facet's own filters, which Analytics removes before it counts that facet. */
const OWN: Record<FacetName, readonly (keyof QualityFilters)[]> = {
  source: ['channels'],
  link: ['links', 'campaigns'],
  link_groups: ['links', 'campaigns'],
  country: ['countries'],
};

/** Whether the selection counts credited only, as `creditedOnly` reads it; removing a facet keeps that. Pure. */
const creditedOf = (f: QualityFilters): boolean =>
  creditedOnly({
    channels: f.channels ?? null,
    links: f.links ? { ...f.links, allExcept: f.links.allExcept ?? null } : null,
    campaigns: f.campaigns ? { op: f.campaigns.mode, values: f.campaigns.values } : null,
  });

/** What a facet's counts depend on: the draft without the facet's own filters, and whether it counts credited only. */
const countedBy = (name: FacetName, filters: QualityFilters): { filters: QualityFilters; credited: boolean } => {
  const rest: QualityFilters = { ...filters };
  OWN[name].forEach((key) => delete rest[key]);
  return { filters: canonicalFilters(rest), credited: creditedOf(filters) };
};

/**
 * The relay's refusals for size that a smaller own filter can clear: too many filter conditions, or Spark's 413 on the
 * owner's body, which carries the own filter. Not `query_limit_exceeded`: Analytics drops the facet's own filter before
 * it builds the statement, so that one waits for the sheet to reopen.
 */
const SIZE_REFUSALS: readonly QualityErrorCode[] = ['filter_limit_exceeded', 'request_too_large'];

/** A facet read for one count signature (`ask`), with the full request it sent (`request`, as JSON). */
type Held = { ask: string; request: string; slot: FacetSlot<QualityFacetData> };

type Sent = { tenantId: number; request: QualityFacetRequest };

/**
 * One picker's counts. The ask (tenant, addresses, range and what the counts depend on) is its count signature: a
 * landed answer is kept for it, so a choice in the facet's own filter asks nothing, a reopened sheet asks again only
 * after a failure or a change, and only the newest signature's answer lands. The request sent carries the full draft.
 * A refusal for size stands only for the request it was given: a draft that differs from it is asked again, because a
 * smaller own filter can clear it. Any other failure, and a read the closing sheet cancelled, is forgotten, so the
 * reopened sheet asks with the draft as it is then.
 */
const useFacetSlot = <N extends FacetName>(name: N, input: QualityFacetsInput): FacetSlot<FacetData<N>> => {
  const { tenantId, enabled, selectedHosts, range, filters, wanted } = input;
  const scope = tenantId !== undefined && enabled && selectedHosts?.length ? { tenantId, selectedHosts } : null;
  const ask = scope
    ? JSON.stringify({
        ...scope,
        range,
        name,
        countingUnit: 'person',
        population: 'unflagged',
        ...countedBy(name, filters),
      })
    : '';
  // The request for the draft as it is now.
  const draft: Sent | null = scope
    ? {
        tenantId: scope.tenantId,
        request: facetRequest({ name } as QualityDiscoveryFacet, range, scope.selectedHosts, canonicalFilters(filters)),
      }
    : null;
  const full = draft ? JSON.stringify(draft) : '';
  const active = ask !== '' && wanted.includes(name);
  const [held, setHeld] = React.useState<Held | null>(null);
  // A mode flip keeps no answer (the signature is the same either side of it): back in quality mode it is read again.
  // A read that did not land is forgotten once the facet is not asked for: the next ask sends the draft as it is then.
  if (held !== null && (!enabled || (!active && held.slot.status !== 'ready'))) setHeld(null);
  const refused = held?.slot.code !== undefined && SIZE_REFUSALS.includes(held.slot.code) && held.request !== full;
  const mine = held?.ask === ask && !refused ? held : null;
  const landed = mine?.slot.status === 'ready';
  // The request this signature asks: the one already on its way or answered, else the draft as it is now. It stays the
  // same while only the facet's own filter changes, so that change neither cancels nor repeats a read.
  const asking = mine ? mine.request : full;

  // Syncs with the analytics service: this picker's facet, through the tenant's queue.
  React.useEffect(() => {
    if (!active || landed || asking === '') return undefined;
    const { tenantId: id, request } = JSON.parse(asking) as Sent;
    const controller = new AbortController();
    setHeld({ ask, request: asking, slot: LOADING });
    enqueueQuality(id, `facet:${name}`, () => postTrafficQuality(id, request, controller.signal), controller.signal)
      .then((answer) => {
        if (controller.signal.aborted) return;
        const facet = 'facet' in answer && answer.facet.name === name ? answer.facet : null;
        setHeld({ ask, request: asking, slot: facet ? { status: 'ready', data: facet } : ERROR });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        const code = err instanceof QualityError ? err.code : null;
        setHeld({ ask, request: asking, slot: code ? { ...ERROR, code } : ERROR });
      });
    return () => controller.abort();
  }, [active, landed, ask, asking, name]);

  const slot = mine ? mine.slot : active ? LOADING : IDLE;
  return slot as FacetSlot<FacetData<N>>;
};

/**
 * The facets the sheet has shown since it opened, with those it shows now: moving to another section cancels nothing,
 * and closing the sheet forgets them all (which cancels every wait).
 */
const useAskedFacets = (open: boolean, shown: readonly FacetName[]): readonly FacetName[] => {
  const [asked, setAsked] = React.useState<readonly FacetName[]>([]);
  const now = open ? [...asked, ...shown.filter((name) => !asked.includes(name))] : [];
  // Kept while rendering (state adjusted to its props): it only grows while the sheet is open, and empties on close.
  if (now.length !== asked.length) setAsked(now);
  return now;
};

/** The Filters sheet's picker counts, read through the tenant's queue after the report has settled. */
export const useQualityFacets = (input: QualityFacetsInput): QualityFacetsState => {
  const kept = { ...input, wanted: useAskedFacets(input.open, input.wanted) };
  return {
    sources: useFacetSlot('source', kept),
    links: useFacetSlot('link', kept),
    countries: useFacetSlot('country', kept),
    linkGroups: useFacetSlot('link_groups', kept),
  };
};

/** An ID a group can hold: 1-64 ASCII letters or digits. */
const GROUP_ID = /^[A-Za-z0-9]{1,64}$/;

/**
 * An affiliate's or campaign's badge from the `link_groups` lists (campaigns compared in lower case): its count, 0
 * when missing from an untruncated list, null (`-`) when missing from a truncated list, for an ID no group can hold,
 * or when the lists are `null` or not read.
 */
export const badgeCount = (groups: LinkGroups | null, list: 'affiliates' | 'campaigns', id: string): number | null => {
  const rows = groups?.[list];
  if (!groups || !rows || !GROUP_ID.test(id)) return null;
  const want = list === 'campaigns' ? id.toLowerCase() : id;
  const row = rows.find((r) => (list === 'campaigns' ? r.id.toLowerCase() : r.id) === want);
  if (row) return row.people;
  const truncated = list === 'affiliates' ? groups.affiliatesTruncated : groups.campaignsTruncated;
  return truncated ? null : 0;
};
